import { TeamOutlined } from '@ant-design/icons';
import { Button, ButtonProps } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { GoHighLevelIcon } from '@mm/icons';
import { useGoHighLevelCRUDActions } from '@mm/state/actions/GoHighLevelCRUDActions';
import { useSuppressionListsCRUDActions } from '@mm/state/actions/SuppressionListsCRUDActions';
import ConnectionModel, {
  AccessToken,
  ConnectionKind,
} from '@mm/state/model/ConnectionModel';
import { selectGoHighLevelSuppressionList } from '@mm/state/modules/suppressionListsTableState/selectors';
import { selectCrmsLeadsSent } from '@mm/state/selectors/connectionsSelectors';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationEmptyState } from '../../../BaseIntegration/BaseIntegrationEmptyState';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { Help } from '../KlaviyoIntegrations/KlaviyoHelpLink';
import { OauthTokenConnection } from '../OauthTokenConnection/OauthTokenConnection';
import { OauthTokenStatus } from '../OauthTokenStatus/OauthTokenStatus';
import styles from './GoHighLevelIntegrations.module.css';
import { GoHighLevelSettings } from './GoHighLevelSettings';
import { GoHighLevelSuppressionSetup } from './GoHighLevelSuppressionSetup';
import { useConnectGoHighLevel } from './use-connect-gohighlevel';

export const tokenStatus = (
  token: ConnectionModel['gohighlevelOauthToken'],
) => <OauthTokenStatus token={token} styles={styles} />;

export const GoHighLevelIntegration = ({
  version,
  connection,
}: {
  version: 'connections' | 'suppression' | 'widget';
  connection?: ConnectionModel;
}) => {
  const suppressions = useSelector(selectGoHighLevelSuppressionList);

  const suppression = suppressions[0];
  const isWidgetVersion = version === 'widget';

  const [accountsFetched, setAccountsFetched] = useState(false);
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const oAuthButtonName = 'Add a GoHighLevel Account';
  const otherProps = {
    landmineName: landmineNames.GOHIGHLEVEL_UPGRADE,
    featureFlag: 'nativeIntegrations',
  };
  const { invalidateStore } = useGoHighLevelCRUDActions();
  const {
    accounts,
    getGoHighLevelAccounts,
    goHighLevelConnections,
    getGoHighLevelConnections,
    connectGoHighLevelToken,
    revoke,
  } = useConnectGoHighLevel();
  const usedToken = connection
    ? connection?.gohighlevelOauthToken || accounts[0]
    : goHighLevelConnections.filter(
        c => c.kind === ConnectionKind.AUDIENCE,
      )?.[0]?.gohighlevelOauthToken;
  const usedTokenId = usedToken?.id;
  const connectDisabled = (accounts || []).find(
    acc => acc.id === usedTokenId,
  )?.readOnly;

  if (usedToken) {
    usedToken.readOnly = !!connectDisabled;
  }

  const tokenToAccessToken = {
    id: usedTokenId,
    errorInfo: usedToken?.errorInfo,
    readOnly: usedToken?.readOnly,
    status: 'active',
  } as AccessToken;

  const connectToken = async () => {
    await connectGoHighLevelToken(usedTokenId);
  };

  useEffect(() => {
    if (accountsFetched) {
      getGoHighLevelConnections();
    }
  }, [accountsFetched]);

  useEffect(() => {
    (async () => {
      await getGoHighLevelAccounts();
      setAccountsFetched(true);
    })();
    return () => {
      invalidateStore();
    };
  }, []);

  const oAuthButton = (
    buttonName?: string | ReactNode,
    buttonProps?: Pick<ButtonProps, 'size' | 'color' | 'css'>,
  ) => {
    const defaultButtonProps = {
      css: {
        container: 'integrations__header-add-button-secondary',
      },
    };

    return (
      <Button
        {...(buttonProps || defaultButtonProps)}
        onClick={e => {
          e?.stopPropagation();
          connectToken();
        }}
      >
        {buttonName || 'Set Up Connection'}
      </Button>
    );
  };

  const emptyStateConfig = {
    button: hasNativeIntegrationsFeature
      ? oAuthButton(oAuthButtonName, {
          css: { container: 'integrations__empty-state-button' },
        })
      : oAuthButtonName,
    icon: <GoHighLevelIcon className="integrations__gohighlevel-icon-big" />,
    ...otherProps,
  };

  const { invalidateStore: resetSuppressionsStore } =
    useSuppressionListsCRUDActions();

  const onRevoke = (tokenId: number) => {
    revoke(tokenId as number);
    resetSuppressionsStore();
  };

  const crmsLeadsSent = useSelector(selectCrmsLeadsSent);
  const leadsSent = crmsLeadsSent?.gohighlevelContactsSent;

  const helpLink =
    'https://customers.ai/help/l/en/article/a0ro35f54w-highlevel-integration';

  const header = (
    <div className={styles.integrationHeading}>
      {!isWidgetVersion && <Help link={helpLink} text="Help Center" />}
      {usedToken ? (
        <div className={styles.integrationHeading}>
          {version === 'suppression' ? (
            <>
              <span>Profiles</span>
              <span className={styles.suppressionsCount}>
                {suppression?.profilesCount || 0}
              </span>
            </>
          ) : leadsSent ? (
            <div className="integrations__header-status">
              <TeamOutlined className="integrations__header-status-icon" />
              <div className="integrations__header-status-count">
                {leadsSent}
              </div>
              Leads sent
            </div>
          ) : null}
        </div>
      ) : (
        oAuthButton(connectToken)
      )}
    </div>
  );

  if (isWidgetVersion) {
    return usedToken ? (
      <div className={styles.widgetStatus}>
        <div className={styles.title}>Account</div>
        <div className={styles.statusRow}>
          <OauthTokenStatus
            token={usedToken}
            styles={{ oauthStatus: styles.oauthStatus }}
          />
          <Help link={helpLink} text="Help Center" />
        </div>
      </div>
    ) : (
      <BaseIntegrationEmptyState {...emptyStateConfig} {...otherProps} />
    );
  }

  return (
    <BaseIntegration
      titleComponent={
        <BaseIntegrationHeader
          title="HighLevel"
          upgradeButtonName={
            !connectDisabled ? 'Add an Account' : 'Add a Connection'
          }
          icon={
            <GoHighLevelIcon className="integrations__gohighlevel-header-icon" />
          }
          headerRightComponent={header}
          {...otherProps}
        />
      }
      integrationType="gohighlevel"
      emptyStateConfig={emptyStateConfig}
      showEmptyState={!usedToken}
    >
      <OauthTokenConnection
        helpLink={helpLink}
        controlsDisabled={!!connectDisabled}
        token={tokenToAccessToken}
        revoke={onRevoke}
        connectToken={connectToken}
        className={styles.tokenConnection}
      />
      {usedToken &&
        (version === 'suppression' ? (
          <GoHighLevelSuppressionSetup
            connectDisabled={!!connectDisabled}
            tokenId={usedToken.id}
          />
        ) : (
          <GoHighLevelSettings
            connections={goHighLevelConnections}
            account={usedToken}
          />
        ))}
    </BaseIntegration>
  );
};
